import { ProductListCard } from './productListCard';
import { hasElements, rm, withCurrentPage } from 'utils';
import { Cell, Grid } from 'components/Grid';
import { Section } from 'components/Section';
import cn from 'classnames';
import styles from './styles.scss';
import { useSelector } from 'react-redux';
import { isMobile as isMobileSelector } from 'components/Shared/responsive';
import { useMaxVariants } from 'views/ProductlistPage/useMaxVariants';
import { useProductlist } from 'views/ProductlistPage/useProductList';
import { isHero } from 'views/ProductlistPage/selectors';
import { ProductlistViewProduct } from 'models/ProductlistViewProduct';
import { TrackedElement, useIntersectionObserver } from 'hooks/useIntersectionObserver';
import { GtmPromotion } from 'store/googleTagManager/types';
import { getSearchPhrase } from 'views/KnowledgebasePage/selector';
import { getThecaPromotion } from 'store/googleTagManager/events/utils';

export type GtmViewItem = {
  product: ProductlistViewProduct;
  index: number;
};

type ProductListProps = {
  gtmOnItemClick: (product: ProductlistViewProduct, index: number, pageNbr: number) => void;
  gtmOnViewItem: (items: GtmViewItem[], pageNbr: number) => void;
  gtmOnViewTheca?: (items: GtmPromotion[]) => void;
  gtmOnSelectTheca?: (item: GtmPromotion) => void;
};

const handleInternalVisibility = (
  elements: TrackedElement<ProductlistViewProduct>[],
  products: ProductlistViewProduct[]
) => {
  return elements.map(element => {
    const viewedProduct = element.payload;
    const productIndex = products.indexOf(viewedProduct);

    return {
      index: productIndex,
      product: viewedProduct
    };
  });
};

const NUMBER_OF_IMAGES_TO_LOAD_DIRECTLY = 3;

export const ProductList = ({
  gtmOnItemClick,
  gtmOnViewItem,
  gtmOnSelectTheca,
  gtmOnViewTheca
}: ProductListProps) => {
  const { pages, products, pageNbr } = useProductlist();
  const [maxVariants, ref] = useMaxVariants();
  const isMobile = useSelector(isMobileSelector);
  const showHero = useSelector(withCurrentPage(isHero));
  const searchPhrase = useSelector(getSearchPhrase);
  const isSponsored = false;

  const { setElementRef } = useIntersectionObserver({
    onBatchVisible: handleElementsVisible
  });

  function handleElementsVisible(elements: TrackedElement<ProductlistViewProduct>[]) {
    const internalItems: TrackedElement<ProductlistViewProduct>[] = [];
    const thecaItems: GtmPromotion[] = [];

    elements.forEach(element => {
      if (element.payload.isThecaProduct) {
        thecaItems.push(getThecaPromotion(element.payload.productId, true, searchPhrase));
      } else {
        internalItems.push(element);
      }
    });

    if (gtmOnViewTheca) {
      gtmOnViewTheca(thecaItems);
    }
    gtmOnViewItem(handleInternalVisibility(internalItems, products), pageNbr);
  }

  if (!hasElements(products) && isMobile) {
    return (
      <div className={styles.noProductContainer}>
        <p>{rm('no_products_message')}</p>
      </div>
    );
  }

  function handleOnItemClick(product: ProductlistViewProduct, index: number) {
    if (product.isThecaProduct && gtmOnSelectTheca) {
      gtmOnSelectTheca(getThecaPromotion(product.productId, true, searchPhrase));
    } else {
      gtmOnItemClick(product, index, pageNbr);
    }
  }

  return (
    <Section
      contentClassName={cn({
        [styles.fullWidthGrid]: isMobile,
        [styles.gridSpacing]: !isMobile
      })}
    >
      {hasElements(products) ? (
        pages.map(({ page, products }) => (
          <Grid
            className={styles.grid}
            desktop="4"
            tablet="3"
            phone="1"
            gutters={isMobile ? 'hidden' : 'large'}
            key={page}
          >
            {products.map((product, index) => {
              const isLazy = index >= NUMBER_OF_IMAGES_TO_LOAD_DIRECTLY;
              const isHero = index === 0 && showHero;

              return (
                <Cell
                  className={cn({
                    [styles.cellBorder]: isMobile
                  })}
                  key={product.ticket}
                  desktop={isHero ? '2' : '4'}
                  tablet={isHero ? '1' : '3'}
                  ref={node => setElementRef({ ref: node, payload: product })}
                >
                  <ProductListCard
                    isLazy={isLazy}
                    maxVariants={maxVariants}
                    isSponsored={isSponsored}
                    isListView={isMobile}
                    heroDisplay={isHero}
                    onLinkClick={() => handleOnItemClick(product, index)}
                    ref={ref}
                    {...{
                      product
                    }}
                  />
                </Cell>
              );
            })}
          </Grid>
        ))
      ) : (
        <p>{rm('no_products_message')}</p>
      )}
    </Section>
  );
};
